import Cookies from "js-cookie";
import { LoginResponse } from "./LoginResponse";
import { jwtDecode } from "jwt-decode";
import { UpdatePasswordResponse } from "./UpdatePasswordResponse";
import CryptoJS from "crypto-js";

const apiUrl = process.env.REACT_APP_API_URL;
const apiCode = process.env.REACT_APP_API_CODE;

export async function login(username: string, password: string): Promise<LoginResponse | undefined> {
    try {
        // Hash the password (assuming `CryptoJS` is used for hashing)
        const hashedPassword = CryptoJS.SHA256(password).toString(CryptoJS.enc.Hex);

        // Make the API call
        const response = await fetch(`${apiUrl}/api/login?code=${apiCode}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ username, password: hashedPassword }),
        });

        // Check if the response is ok (status 200)
        if (!response.ok) {
            throw new Error(`Error: ${response.status}`);
        }

        // Parse the JSON response
        const data = await response.json();

        // Extract the token from the response
        const { token } = data;

        // Decode the JWT token to extract the expiration time
        const decodedToken = jwtDecode(token);

        if (decodedToken.exp !== undefined) {
            const tokenExpiration = decodedToken.exp; // exp is in seconds since epoch

            // Calculate the expiration date for the cookie based on the JWT exp claim
            const expires = new Date(tokenExpiration * 1000); // Convert from seconds to milliseconds

            // Set the token cookie using the expiration from the JWT
            Cookies.set('auth_token', token, { expires: expires, secure: true, sameSite: 'Strict' });
        }

        return { token, userNumber: data.userNumber, loginSuccess: true }; // Adjust this as per your API response
    } catch (error) {
        console.error("Login failed:", error);
        return undefined;
    }
}

export async function updatePassword(username: string, password: string, newPassword: string): Promise<UpdatePasswordResponse | undefined> {
    // Hash the password with SHA-256
    const hashedPassword = CryptoJS.SHA256(password).toString(CryptoJS.enc.Hex);
    const hashedNewPassword = CryptoJS.SHA256(newPassword).toString(CryptoJS.enc.Hex);

    try {
        const response = await fetch(`${apiUrl}/api/login?code=${apiCode}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${Cookies.get('auth_token')}`, // Include the auth token
            },
            body: JSON.stringify({
                username: username,        // Username from the decoded token
                password: hashedPassword,       // Current password
                newPassword: hashedNewPassword,   // New password
            }),
        });

        if (response.ok) {
            return { responseText: 'Password updated successfully.', success: true };
        } else {
            const errorData = await response.json();
            return { responseText: 'Failed to update password.', success: false };
        }
    } catch (error) {
        return { responseText: 'An error occurred while updating the password.', success: false };
    }
}